import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, ShieldCheck, TrendingUp, Workflow, Target, FileText, Mail, Cpu, Lock, Sparkles, Linkedin, Copy } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";

export default function Landing() {
  const nav = useNavigate();

  useSEO({
    title: "Calculateur ROI IAM gratuit — Étude personnalisée Ariovis | IAM ROI Calculator",
    description: "Chiffrez en 7 minutes le ROI de votre programme IAM. 3 scénarios documentés (prudent, central, ambitieux), ROI opérationnel + SAM (licences) + risque cyber. Export PDF brandé Ariovis, sans engagement.",
    canonical: "https://iam-calculator.emergent.host/",
  });

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = encodeURIComponent(
    "J'ai chiffré le ROI d'un programme IAM en quelques minutes avec le mini-produit d'Ariovis. Try it 👇"
  );

  const onCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Lien copié — partagez-le à un pair !");
  };

  const valueProps = [
    {
      icon: TrendingUp,
      title: "ROI quantifié",
      text: "Trois scénarios — prudent, central, ambitieux — calculés à partir de vos volumes JML, demandes d'accès et exposition cyber.",
    },
    {
      icon: ShieldCheck,
      title: "Risque cyber (ALE)",
      text: "Modèle d'Annual Loss Expectancy aligné NIST / Verizon DBIR / IBM-Ponemon. Pas de promesse, une réduction probabilisée.",
    },
    {
      icon: Workflow,
      title: "Plan d'action",
      text: "À l'issue de l'étude, un plan de déploiement par paliers — la colonne vertébrale IAM avant le passage à l'échelle.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      <Header />

      {/* HERO */}
      <section className="relative">
        <div className="ar-blob bg-fuchsia-500" style={{ width: 460, height: 460, top: -100, right: -100, opacity: 0.35 }} />
        <div className="ar-blob bg-orange-400" style={{ width: 380, height: 380, top: 280, left: -120, opacity: 0.25 }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-8" data-testid="hero-eyebrow">
                <span className="h-px w-8 bg-slate-300" />
                Mini-produit Ariovis · IAM ROI Calculator
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.02]" data-testid="hero-title">
                Chiffrer le <span className="ar-gradient-text">ROI</span> de votre programme <span className="ar-gradient-text">IAM</span>.
                <br />
                En 7 minutes.
              </h1>

              <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="hero-subtitle">
                Un mini-calculateur conçu pour les comités d'investissement. Sept étapes guidées,
                de la population (internes, prestataires, saisonniers) au paysage applicatif et
                à la maturité IAM — nous restituons trois scénarios financiers documentés
                (ROI opérationnel, SAM et risque cyber), sans additionner brutalement les gains,
                sans promesse cyber abstraite.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => nav("/calculer")}
                  data-testid="hero-cta-start"
                  className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950 ar-glow"
                >
                  Lancer mon étude ROI
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href="#methode"
                  className="px-6 py-4 text-sm text-slate-700 hover:text-slate-900 font-medium tracking-tight transition"
                  data-testid="hero-cta-method"
                >
                  Voir la méthode →
                </a>
              </div>

              <div className="mt-12 flex items-center gap-8 text-xs text-slate-500">
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-600" />3 scénarios</div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-slate-900" />Export PDF</div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" />Aucun engagement</div>
              </div>
            </div>

            {/* Sidebar visuel */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-3xl bg-slate-950 p-8 text-white overflow-hidden ar-glow">
                <div className="ar-grid-circuits absolute inset-0 opacity-30" />
                <div className="relative">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 mb-6">Aperçu — scénario central</div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">ROI 4 ans</div>
                      <div className="text-4xl font-bold ar-gradient-text mt-1">+187 %</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">Payback</div>
                      <div className="text-4xl font-bold text-white mt-1">18 <span className="text-base text-slate-400">mois</span></div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">VAN</div>
                      <div className="text-2xl font-semibold text-white mt-1">1,12 M€</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">Bénéfices / an</div>
                      <div className="text-2xl font-semibold text-white mt-1">420 K€</div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-800">
                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                      <span>Opérationnel</span><span>48 %</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-slate-100" style={{ width: "48%" }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-4 mb-2">
                      <span>SAM (licences)</span><span>22 %</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-fuchsia-500" style={{ width: "22%" }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-4 mb-2">
                      <span>Cyber (ALE)</span><span>30 %</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: "30%" }} />
                    </div>
                  </div>

                  <div className="mt-7 text-[10px] text-slate-500 leading-relaxed">
                    Données exemple sur 5 000 utilisateurs · secteur Banque · maturité partielle.
                    Votre étude personnalisée à venir.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALEUR */}
      <section id="valeur" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="mb-14">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">Trois piliers</div>
          <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl">
            Le ROI d'un programme IAM ne se résume jamais à un seul chiffre magique.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {valueProps.map((vp, i) => (
            <div key={i} className="relative group bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:-translate-y-1" data-testid={`value-card-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-6">
                <vp.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3 tracking-tight">{vp.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{vp.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST / À PROPOS */}
      <section id="trust" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">Pourquoi nous faire confiance</div>
            <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              Un mini-produit Ariovis,<br />
              <span className="ar-gradient-text">nourri par 10 ans de terrain.</span>
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              Ce calculateur n'est pas un gadget marketing : il s'appuie sur <b>plus de 10 ans
              de conseil en IAM</b> et un référentiel construit à partir d'<b>au moins une
              centaine de contextes projets</b> différents — secteurs régulés, ETI, grands
              comptes, environnements multi-clouds.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Les hypothèses chargées dans le moteur sont <b>réelles</b>, anonymisées,
              et croisées avec des sources publiques agnostiques (NIST, Verizon DBIR,
              IBM/Ponemon, Gartner).
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <TrustCard
              icon={Sparkles}
              eyebrow="L'IA et nous"
              title="Construit avec l'IA, opéré par des humains."
              text="L'intelligence artificielle a contribué à bâtir ce site. Le site, lui, n'est pas géré par une IA : aucune logique de calcul ni de décision n'est déléguée à un modèle. Les hypothèses ont été validées par les consultants Ariovis."
              testid="trust-card-ai"
            />
            <TrustCard
              icon={Lock}
              eyebrow="Vos données"
              title="Anonymes, jamais envoyées à une IA."
              text="Les volumes et secteurs que vous saisissez restent dans le périmètre du calcul (chiffrement TLS de bout en bout). Aucune donnée n'est transmise à un service d'IA, ni stockée dans une base à des fins d'apprentissage."
              testid="trust-card-privacy"
            />
            <TrustCard
              icon={Cpu}
              eyebrow="Méthode"
              title="Estimations en toute bonne foi."
              text="Quand on agrège autant de données, les spécificités de certains contextes (architectures legacy, contraintes sectorielles fines, dette technique) ne peuvent être prises en compte que par une intervention humaine d'Ariovis."
              testid="trust-card-method"
            />
            <TrustCard
              icon={ShieldCheck}
              eyebrow="Accès"
              title="Utilisation libre, sans engagement."
              text="Vous pouvez utiliser ce calculateur autant de fois que nécessaire, sans création de compte. Il vous appartient — partagez-le librement à vos pairs, comités de direction, RSSI ou DSI."
              testid="trust-card-free"
            />
          </div>
        </div>

        {/* SHARE BLOCK */}
        <div className="mt-16 relative rounded-3xl overflow-hidden bg-slate-50 border border-slate-200 p-8 lg:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">Faites circuler</div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                Si cet outil vous a été utile,<br />
                <span className="ar-gradient-text">partagez-le à un pair.</span>
              </h3>
              <p className="text-slate-600 mt-4 max-w-xl text-sm leading-relaxed">
                Un RSSI, un DSI, un DAF, un directeur de programme IAM : ce calculateur peut
                leur faire gagner plusieurs réunions de cadrage. Le savoir IAM se diffuse mieux
                quand il est mis en commun.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-linkedin"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950"
              >
                <Linkedin className="w-4 h-4" />
                Partager sur LinkedIn
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent("Calculateur ROI IAM — Ariovis")}&body=${shareText}%0A%0A${encodeURIComponent(shareUrl)}`}
                data-testid="share-email"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-white text-slate-900 border border-slate-200 hover:border-slate-300 font-medium tracking-tight transition-all"
              >
                <Mail className="w-4 h-4" />
                Partager par email
              </a>
              <button
                onClick={onCopy}
                data-testid="share-copy"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-white text-slate-700 border border-slate-200 hover:border-slate-300 font-medium tracking-tight transition-all text-sm"
              >
                <Copy className="w-4 h-4" />
                Copier le lien
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ - SEO boost */}
      <section id="faq" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">Questions fréquentes</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
              Tout savoir<br /><span className="ar-gradient-text">avant de cliquer.</span>
            </h2>
            <p className="text-sm text-slate-500 mt-6 max-w-sm leading-relaxed">
              Calculateur ROI IAM, méthodologie ALE, gouvernance des accès, SAM,
              Netwrix Identity Manager : les réponses aux questions qu'on nous
              pose le plus souvent.
            </p>
          </div>
          <div className="lg:col-span-8 space-y-3">
            {[
              {
                q: "Sur quelles données s'appuie le calculateur ROI IAM ?",
                a: "Le moteur s'appuie sur le savoir-faire d'Ariovis (10+ ans de conseil IAM, plus d'une centaine de contextes projets) et sur des sources publiques agnostiques : NIST, Verizon DBIR, IBM-Ponemon, Gartner, Flexera, ITAM Forum. Les hypothèses ont été validées par les consultants Ariovis.",
              },
              {
                q: "Que contient le PDF d'étude envoyé ?",
                a: "Une étude complète au format PDF brandé Ariovis : les 3 scénarios (prudent, central, ambitieux) avec ROI, VAN et payback ; la décomposition par poste (JML, demandes d'accès, mots de passe, revues, audit, PAM, SAM, cyber) ; la méthodologie de calcul ; l'estimation indicative Ariovis pour Netwrix Identity Manager ; le plan de déploiement en 8 lots.",
              },
              {
                q: "Pourquoi 3 scénarios et pas un seul chiffre ?",
                a: "Le ROI d'un programme IAM ne se résume jamais à un seul chiffre magique. Nous présentons un scénario prudent (hypothèses minimalistes), un central (notre recommandation), et un ambitieux (déploiement étendu). Cette approche est alignée sur les standards NIST / Gartner et permet d'arbitrer en comité d'investissement.",
              },
              {
                q: "Qu'est-ce que le ROI SAM intégré au calculateur ?",
                a: "Le SAM (Software Asset Management) est la 2ème source d'économies du programme IAM, souvent oubliée : récupération des licences inactives (Flexera estime 25-37 % de licences sous-utilisées en moyenne), récupération sur les leavers (licences orphelines), évitement d'achats neufs par réaffectation. Notre calculateur l'intègre comme un bucket distinct.",
              },
              {
                q: "Comment est calculé le ROI risque cyber ?",
                a: "Nous appliquons le modèle ALE (Annual Loss Expectancy) : probabilité × impact. Quatre scénarios sont pondérés : compromission utilisateur, compromission privilégiée, abus de compte orphelin, fuite de données. La réduction = ALE_avant × (1 − efficacité × couverture × adoption). Le ROI risque n'est pas plafonné car il représente une perte espérée annualisée.",
              },
              {
                q: "Mes données sont-elles envoyées à une IA ?",
                a: "Non. L'IA a contribué à construire ce site, mais le calcul est 100 % déterministe et exécuté sur les serveurs Ariovis. Aucune donnée saisie n'est transmise à un service d'intelligence artificielle, ni utilisée pour de l'apprentissage.",
              },
              {
                q: "Le calculateur est-il vraiment gratuit ?",
                a: "Oui, son utilisation est libre et sans engagement. Vous pouvez générer autant d'études PDF que nécessaire, sans création de compte. Une session de cadrage de 60 minutes vous est offerte par Ariovis pour transformer cette étude en plan de déploiement Netwrix Identity Manager.",
              },
            ].map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="group bg-white border border-slate-200 rounded-2xl p-6 transition-all hover:border-slate-300"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                  <h3 className="text-base font-semibold text-slate-900 tracking-tight leading-snug">
                    {item.q}
                  </h3>
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full bg-slate-100 group-hover:bg-fuchsia-100 flex items-center justify-center transition">
                    <span className="text-slate-700 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </span>
                </summary>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* METHODE */}
      <section id="methode" className="relative bg-slate-950 text-white overflow-hidden">
        <div className="ar-grid-circuits absolute inset-0 opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-4">Méthodologie</div>
              <h2 className="text-3xl lg:text-5xl font-bold tracking-tight leading-tight">
                Quatre familles de valeur. <span className="ar-gradient-text">Zéro double comptabilisation.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-8">
              {[
                { n: "01", t: "Gains opérationnels", d: "Automatisation du cycle de vie (Joiner, Mover, Leaver), demandes d'accès, resets, revues, rotation PAM. Volume × temps × coût horaire × taux d'automatisation." },
                { n: "02", t: "Productivité Day-1", d: "Temps d'attente d'accès évité pour les nouveaux arrivants — un coût caché souvent sous-estimé." },
                { n: "03", t: "Réduction du risque cyber (ALE)", d: "ALE = probabilité × impact. Compromission utilisateur, privilégiée, abus d'orphelin, fuite — pondérés par contrôles, couverture et adoption." },
                { n: "04", t: "Compliance & audit", d: "Réduction de l'effort de preuve, des findings et du coût des revues d'accès — preuves automatisables." },
              ].map((b) => (
                <div key={b.n} className="flex gap-6">
                  <div className="text-3xl font-bold ar-gradient-text shrink-0 w-14">{b.n}</div>
                  <div>
                    <div className="text-lg font-semibold mb-1.5 tracking-tight">{b.t}</div>
                    <div className="text-sm text-slate-400 leading-relaxed">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">Prêt à objectiver ?</div>
            <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              7 étapes. <br />Un PDF. <span className="ar-gradient-text">Une décision documentée.</span>
            </h2>
            <p className="text-slate-600 leading-relaxed max-w-lg">
              Sept étapes guidées : secteur, population (internes + prestataires + saisonniers),
              flux RH, applications, maturité IAM, paramètres avancés (outil existant, audit, accès Day-1),
              coordonnées. Votre étude PDF brandée vous est envoyée par email immédiatement.
            </p>
            <button
              onClick={() => nav("/calculer")}
              data-testid="bottom-cta-start"
              className="group mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950 ar-glow"
            >
              Démarrer l'étude
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { i: Target, t: "Périmètre", d: "7 étapes · 20+ questions", c: "text-fuchsia-600" },
              { i: FileText, t: "Rendu", d: "PDF brandé Ariovis", c: "text-orange-500" },
              { i: Mail, t: "Diffusion", d: "Reçu par email", c: "text-slate-900" },
              { i: ShieldCheck, t: "Souveraineté", d: "Données non persistantes", c: "text-fuchsia-600" },
            ].map((x, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <x.i className={`w-5 h-5 ${x.c} mb-4`} />
                <div className="text-sm font-semibold text-slate-900">{x.t}</div>
                <div className="text-xs text-slate-500 mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function TrustCard({ icon: Icon, eyebrow, title, text, testid }) {
  return (
    <div data-testid={testid} className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-300 transition-all">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-1">{eyebrow}</div>
          <div className="text-base font-semibold text-slate-900 tracking-tight leading-snug">{title}</div>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{text}</p>
        </div>
      </div>
    </div>
  );
}
